import {
  ChevronDown,
  ChevronUp,
  Globe,
  Loader2,
  Notebook,
  Phone,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  Combobox,
  ComboboxLabel,
  ComboboxOption,
} from '@/components/ui/catalyst/combobox';
import {
  Description,
  Field,
  Label,
} from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Radio, RadioField, RadioGroup } from '@/components/ui/catalyst/radio';
import {
  type PatchProviderServiceRequestTemplateSchema,
  useProviderServicesRequestTemplateGetQuery,
  useProviderServicesRequestTemplatePatchMutation,
} from '@/services/api/generated/providerDestinationsService';
import { useLazySearchProviderServicesQuery } from '@/services/api/providerDirectoryV2Service';
import type { ProviderDirectoryV2SearchResult } from '@/services/types/provider-directory-v2-types';
import {
  RequestTemplateIntent,
  ResearchStatus,
} from '@/types/provider-destinations';

interface RequestTemplatePanelProps {
  providerServiceId: string;
  onSaved?: () => void;
}

// Debounce helper for auto-save
const AUTOSAVE_DELAY = 800;

export const RequestTemplatePanel = ({
  providerServiceId,
  onSaved,
}: RequestTemplatePanelProps) => {
  const {
    data: template,
    isLoading,
    isError,
  } = useProviderServicesRequestTemplateGetQuery(
    { providerServiceId },
    { skip: !providerServiceId },
  );

  const [updateTemplate] = useProviderServicesRequestTemplatePatchMutation();

  // Local form state
  const [intent, setIntent] = useState<RequestTemplateIntent>(
    RequestTemplateIntent.STANDARD,
  );
  const [billsSameProvider, setBillsSameProvider] = useState(true);
  const [billingOrgId, setBillingOrgId] = useState<string | null>(null);
  const [billingOrgName, setBillingOrgName] = useState<string>('');
  const [hintPhone, setHintPhone] = useState('');
  const [hintWebsite, setHintWebsite] = useState('');
  const [hintNotes, setHintNotes] = useState('');
  const [hintsOpen, setHintsOpen] = useState(false);

  // Billing org search
  const [billingSearchQuery, setBillingSearchQuery] = useState('');
  const [searchBillingOrgs, { data: billingSearchResults, isFetching: isBillingSearching }] =
    useLazySearchProviderServicesQuery();

  const billingOptions: ProviderDirectoryV2SearchResult[] =
    (billingSearchResults?.results ?? []).filter(
      (result) => result.provider_service.id !== providerServiceId,
    );

  // Sync from server
  useEffect(() => {
    if (!template) return;
    setIntent(template.template_intent);
    setBillsSameProvider(template.billing_mode === 'SAME_PROVIDER');
    setBillingOrgId(template.billing_service_ref_id ?? null);
    setHintPhone(template.research_hint_main_phone ?? '');
    setHintWebsite(template.research_hint_website ?? '');
    setHintNotes(template.research_hint_notes ?? '');
    // Open hints if any are populated
    if (
      template.research_hint_main_phone ||
      template.research_hint_website ||
      template.research_hint_notes
    ) {
      setHintsOpen(true);
    }
  }, [template]);

  // Auto-save logic
  const saveTimerRef = useRef<number | null>(null);
  const pendingPatchRef = useRef<PatchProviderServiceRequestTemplateSchema>({});

  const scheduleSave = useCallback(
    (patch: PatchProviderServiceRequestTemplateSchema) => {
      pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };

      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
      }

      saveTimerRef.current = window.setTimeout(async () => {
        const patchToSend = { ...pendingPatchRef.current };
        pendingPatchRef.current = {};

        try {
          await updateTemplate({
            providerServiceId,
            patchedPatchProviderServiceRequestTemplateSchema: patchToSend,
          }).unwrap();
          onSaved?.();
        } catch {
          toast.error('Failed to save template changes');
        }
      }, AUTOSAVE_DELAY);
    },
    [providerServiceId, updateTemplate, onSaved],
  );

  // Billing org search debounce
  useEffect(() => {
    const trimmed = billingSearchQuery.trim();
    if (trimmed.length < 2) return;
    const timer = window.setTimeout(() => {
      searchBillingOrgs({ q: trimmed, limit: 10 });
    }, 300);
    // eslint-disable-next-line consistent-return
    return () => window.clearTimeout(timer);
  }, [billingSearchQuery, searchBillingOrgs]);

  // Field change handlers
  const handleIntentChange = (newIntent: string) => {
    const val = newIntent as RequestTemplateIntent;
    setIntent(val);
    scheduleSave({ template_intent: val });
  };

  const handleBillsSameProviderChange = (same: string) => {
    const isSame = same === 'same';
    setBillsSameProvider(isSame);

    if (isSame) {
      setBillingOrgId(null);
      setBillingOrgName('');
      scheduleSave({
        billing_mode: 'SAME_PROVIDER',
        billing_service_ref_id: null,
      });
    } else {
      // Backend requires DIFFERENT_BILLING_ORG to include a non-null billing_service_ref_id.
      // Wait for explicit selection before patching.
    }
  };

  const handleBillingOrgSelect = (result: ProviderDirectoryV2SearchResult | null) => {
    if (!result) return;
    const { id } = result.provider_service;
    if (id === providerServiceId) return;
    setBillingOrgId(id);
    setBillingOrgName(result.provider_service.name ?? '');
    scheduleSave({
      billing_mode: 'DIFFERENT_BILLING_ORG',
      billing_service_ref_id: id,
    });
  };

  const handleHintPhoneChange = (value: string) => {
    setHintPhone(value);
    scheduleSave({ research_hint_main_phone: value || null });
  };

  const handleHintWebsiteChange = (value: string) => {
    setHintWebsite(value);
    scheduleSave({ research_hint_website: value || null });
  };

  const handleHintNotesChange = (value: string) => {
    setHintNotes(value);
    scheduleSave({ research_hint_notes: value || null });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-zinc-400 animate-spin" />
        <p className="mt-3 text-sm text-zinc-500">Loading template...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Failed to load request template. The provider service may not have one
        configured yet.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Intent selector */}
      <Field>
        <Label>Request type</Label>
        <Description>
          Determines how record requests are structured
        </Description>
        <RadioGroup
          value={intent}
          onChange={handleIntentChange}
          className="mt-3"
        >
          <RadioField>
            <Radio value={RequestTemplateIntent.STANDARD} color="blue" />
            <Label>Standard</Label>
            <Description>
              Single facility handling records and bills
            </Description>
          </RadioField>
          <RadioField>
            <Radio value={RequestTemplateIntent.HOSPITAL} color="blue" />
            <Label>Hospital</Label>
            <Description>
              Hospital with separate billing, radiology, and ER departments
            </Description>
          </RadioField>
        </RadioGroup>
      </Field>

      {/* Bills handled by */}
      <Field>
        <Label>Billing organization</Label>
        <Description>Who handles bills for this provider?</Description>
        <RadioGroup
          value={billsSameProvider ? 'same' : 'different'}
          onChange={handleBillsSameProviderChange}
          className="mt-3"
        >
          <RadioField>
            <Radio value="same" color="blue" />
            <Label>Same provider</Label>
          </RadioField>
          <RadioField>
            <Radio value="different" color="blue" />
            <Label>Different billing organization</Label>
          </RadioField>
        </RadioGroup>

        {!billsSameProvider && (
          <div className="mt-3">
            <Combobox
              options={billingOptions}
              value={
                billingOrgId
                  ? billingOptions.find(
                      (o) => o.provider_service.id === billingOrgId,
                    ) ?? null
                  : null
              }
              onChange={handleBillingOrgSelect}
              displayValue={(opt) =>
                opt?.provider_service.name ?? billingOrgName ?? ''
              }
              placeholder="Search billing organizations..."
              onInputChange={setBillingSearchQuery}
              panelClassName="z-[70] max-h-64"
            >
              {(option: ProviderDirectoryV2SearchResult) => (
                <ComboboxOption value={option}>
                  <ComboboxLabel>
                    <div>
                      <div className="font-medium group-data-focus/option:text-white">
                        {option.provider_service.name}
                      </div>
                      {option.clinical_location?.address
                        ?.formatted_address && (
                        <div className="text-xs text-zinc-500 group-data-focus/option:text-blue-100 dark:text-zinc-400">
                          {
                            option.clinical_location.address
                              .formatted_address
                          }
                        </div>
                      )}
                    </div>
                  </ComboboxLabel>
                </ComboboxOption>
              )}
            </Combobox>
            {isBillingSearching && (
              <p className="mt-1 text-xs text-zinc-500">Searching...</p>
            )}
          </div>
        )}
      </Field>

      {/* Research status banner */}
      {template && (
        <ResearchStatusBanner
          status={
            // The template doesn't have research_status directly; show a generic banner
            // if we had the destinations data. For now show a neutral message.
            ResearchStatus.NOT_STARTED
          }
        />
      )}

      {/* Hints section (collapsible) */}
      <div className="rounded-lg border border-zinc-200">
        <button
          type="button"
          onClick={() => setHintsOpen(!hintsOpen)}
          className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-zinc-900 hover:bg-zinc-50"
        >
          <span>Research hints</span>
          {hintsOpen ? (
            <ChevronUp className="h-4 w-4 text-zinc-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-zinc-500" />
          )}
        </button>
        {hintsOpen && (
          <div className="border-t border-zinc-200 px-4 py-4 space-y-4">
            <Field>
              <Label className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" />
                Phone
              </Label>
              <Input
                value={hintPhone}
                onChange={(e) => handleHintPhoneChange(e.target.value)}
                placeholder="e.g. (555) 123-4567"
              />
            </Field>
            <Field>
              <Label className="flex items-center gap-1.5">
                <Globe className="h-3.5 w-3.5" />
                Website
              </Label>
              <Input
                value={hintWebsite}
                onChange={(e) => handleHintWebsiteChange(e.target.value)}
                placeholder="e.g. https://provider.com/records"
              />
            </Field>
            <Field>
              <Label className="flex items-center gap-1.5">
                <Notebook className="h-3.5 w-3.5" />
                Notes
              </Label>
              <Input
                value={hintNotes}
                onChange={(e) => handleHintNotesChange(e.target.value)}
                placeholder="Any additional notes for research..."
              />
            </Field>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const ResearchStatusBanner = ({ status }: { status: ResearchStatus }) => {
  const config: Record<
    ResearchStatus,
    { bg: string; text: string; label: string; description: string }
  > = {
    [ResearchStatus.NOT_STARTED]: {
      bg: 'bg-zinc-50 border-zinc-200',
      text: 'text-zinc-700',
      label: 'Research Integration coming soon',
      description:
        'Destination research automation is launching soon.',
    },
    [ResearchStatus.IN_PROGRESS]: {
      bg: 'bg-blue-50 border-blue-200',
      text: 'text-blue-700',
      label: 'Research in progress',
      description:
        'Our team is researching fax numbers and contact information.',
    },
    [ResearchStatus.READY]: {
      bg: 'bg-emerald-50 border-emerald-200',
      text: 'text-emerald-700',
      label: 'Research complete',
      description: 'Destinations have been verified and are ready to use.',
    },
    [ResearchStatus.FAILED]: {
      bg: 'bg-red-50 border-red-200',
      text: 'text-red-700',
      label: 'Research failed',
      description:
        'We were unable to find valid contact information. Manual entry may be required.',
    },
  };

  const c = config[status];

  return (
    <div className={`rounded-lg border ${c.bg} p-3`}>
      <div className={`text-sm font-medium ${c.text}`}>{c.label}</div>
      <p className={`mt-0.5 text-xs ${c.text} opacity-80`}>{c.description}</p>
    </div>
  );
};
